import {
  ArrowLeftCircleIcon,
  ArrowRightCircleIcon,
  POIIcon
} from "@/assets/icons";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  useCarousel
} from "@/components/ui/carousel";
import { MaskIcon } from "@/components/ui/MaskIcon";
import { cn } from "@/lib/utils";
import { PoiIndustry } from "@/types/poiParameter";

interface IndustryRibbonProps {
  industries: PoiIndustry[];
  activeIndustry: string;
  isLoading: boolean;
  onSelect: (industryId: string) => void;
}

const skeletonCount = 16; // Number of skeleton items to show while loading

const IndustrySkeleton = () => {
  return (
    <div
      data-testid="industry-skeleton"
      className="flex flex-col items-center gap-2 min-w-[60px] pb-2 w-[76px]"
    >
      <div className="p-3 rounded-2xl border border-gainsboro bg-gray-100 w-12 h-12 animate-pulse" />
      <div className="h-3 w-12 bg-gray-200 rounded animate-pulse" />
    </div>
  );
};

const CustomCarouselButton = () => {
  const { canScrollNext, canScrollPrev, scrollNext, scrollPrev } =
    useCarousel();
  return (
    <>
      <button
        type="button"
        aria-label="Previous slide"
        className="absolute left-0 h-7 w-7 rounded-full top-1/4 -translate-y-1/4 fill-dim-gray disabled:fill-gainsboro"
        onClick={scrollPrev}
        disabled={!canScrollPrev}
      >
        <ArrowLeftCircleIcon className="size-7" />
      </button>

      <button
        type="button"
        aria-label="Next slide"
        className="absolute h-7 w-7 rounded-full right-0 top-1/4 -translate-y-1/4 fill-dim-gray disabled:fill-gainsboro"
        onClick={scrollNext}
        disabled={!canScrollNext}
      >
        <ArrowRightCircleIcon className="size-7" />
      </button>
    </>
  );
};

export const IndustryRibbon = ({
  industries,
  activeIndustry,
  isLoading,
  onSelect
}: IndustryRibbonProps) => {
  const getIcon = (iconUrl: string | null, isActive: boolean) => {
    if (iconUrl) {
      return (
        <MaskIcon
          url={iconUrl}
          className={cn(
            "w-6 h-6",
            isActive ? "text-teal-500" : "text-dim-gray"
          )}
        />
      );
    }
    // Using POIIcon as generic fallback
    return (
      <POIIcon
        className={cn("w-6 h-6", isActive ? "fill-teal-500" : "fill-dim-gray")}
      />
    );
  };

  return (
    <Carousel
      opts={{
        align: "start",
        dragFree: true
      }}
      className="w-full"
    >
      <CarouselContent className="-ml-2" classNameContainer="px-10">
        {isLoading
          ? Array.from({ length: skeletonCount }).map((_, i) => (
              <CarouselItem
                key={`skeleton-${i}`}
                className="basis-auto pl-2"
                data-testid={`industry-skeleton-${i}`}
              >
                <IndustrySkeleton />
              </CarouselItem>
            ))
          : industries.map((industry) => {
              const isActive = activeIndustry === industry.code;
              return (
                <CarouselItem key={industry.code} className="basis-auto pl-2">
                  <button
                    onClick={() => onSelect(industry.code)}
                    title={industry.name}
                    className={cn(
                      "flex flex-col items-center gap-2 min-w-[60px] pb-2 transition-all relative w-[94px]",
                      isActive
                        ? "text-teal-500"
                        : "text-dim-gray hover:text-gray-700"
                    )}
                  >
                    <div
                      className={cn(
                        "p-3 rounded-2xl border",
                        isActive
                          ? "bg-teal-50 border-teal-500"
                          : "bg-white border-gainsboro"
                      )}
                    >
                      {getIcon(industry.icon_url, isActive)}
                    </div>
                    <span
                      className={cn("text-[10px] font-semibold line-clamp-2")}
                    >
                      {industry.name}
                    </span>
                  </button>
                </CarouselItem>
              );
            })}
      </CarouselContent>
      <CustomCarouselButton />
    </Carousel>
  );
};
